import React, { useState, useEffect } from 'react';
import Sheet from './ui/Sheet';
import { WarningIcon } from './ui/icons';
import './SubmitSheet.css';

interface SubmitSheetProps {
  isOpen: boolean;
  onClose: () => void;
  completedPubs: number;
  totalPubs: number;
  elapsedTime: string;
  lastScan: { pubName: string; time: string } | null;
  defaultName: string;
  onConfirm: (name: string) => Promise<void>;
}

/**
 * "Submit your crawl?" — leads with the result and the locked-in time so the
 * irreversible part is read before the button. Below two scanned pubs the
 * layout swaps to the discouraging variant: the submit button is demoted to
 * grey and the alternative ("find another pub") is promoted to brass,
 * because the spec drops these entries from the leaderboard entirely.
 */
const SubmitSheet: React.FC<SubmitSheetProps> = ({
  isOpen,
  onClose,
  completedPubs,
  totalPubs,
  elapsedTime,
  lastScan,
  defaultName,
  onConfirm,
}) => {
  const [name, setName] = useState(defaultName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setError(null);
    }
  }, [isOpen, defaultName]);

  const isThin = completedPubs < 2;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(name.trim());
    } catch (e) {
      setError('Something went wrong submitting your crawl. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} title="Submit your crawl?">
      <div className="submit-sheet-summary">
        <span className="submit-sheet-summary-label">
          Final result
          <b>
            {completedPubs} of {totalPubs} pubs
          </b>
        </span>
        <span className="submit-sheet-summary-time tabular">{elapsedTime}</span>
      </div>

      {!isThin && (
        <p className="submit-sheet-note">
          {lastScan ? (
            <>
              Your time was locked in at your last scan —{' '}
              <b>
                {lastScan.pubName}, {lastScan.time}
              </b>
              . Sitting here deciding costs you nothing.
            </>
          ) : (
            'Your time was locked in at your last scan. Sitting here deciding costs you nothing.'
          )}
        </p>
      )}

      {isThin && (
        <div className="submit-sheet-warning">
          <WarningIcon />
          <p>
            <b>This won't reach the leaderboard.</b> You need at least two pubs. One more
            scan and you're on the board.
          </p>
        </div>
      )}

      <div className="submit-sheet-field">
        <label htmlFor="submit-sheet-name">Player or team name</label>
        <div className="submit-sheet-field-wrap">
          <input
            id="submit-sheet-name"
            type="text"
            className="submit-sheet-input"
            data-testid="player-name-input"
            value={name}
            onChange={e => setName(e.target.value.slice(0, 16))}
            maxLength={16}
            disabled={isSubmitting}
          />
          <span className="submit-sheet-count">{name.length}/16</span>
        </div>
      </div>

      {error && <p className="submit-sheet-error">{error}</p>}

      {!isThin ? (
        <>
          <button
            className="submit-sheet-primary"
            data-testid="submit-crawl-confirm"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Submit my crawl'}
          </button>
          <button className="submit-sheet-secondary" onClick={onClose} disabled={isSubmitting}>
            Keep going
          </button>
        </>
      ) : (
        <>
          <button
            className="submit-sheet-primary submit-sheet-primary-demoted"
            data-testid="submit-crawl-confirm"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Submit anyway'}
          </button>
          <button
            className="submit-sheet-secondary submit-sheet-secondary-promoted"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Find another pub
          </button>
        </>
      )}
    </Sheet>
  );
};

export default SubmitSheet;
